import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { z } from 'zod'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { ToolEditor } from '#/components/admin/tool-editor'
import { ToolLogo } from '#/components/admin/tool-logo'
import { DataTable } from '#/components/admin/ui'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { useAdminTools, type ToolRow } from '#/lib/api-admin'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/admin/tools')({ component: adminPage(Page), validateSearch: z.object({ edit: z.string().optional() }) })

function Page() {
  const { edit } = Route.useSearch()
  const navigate = useNavigate()
  const [sel, setSel] = useState<string | null>(edit ?? null)
  const { data, isLoading, isError, isFetching, refetch } = useAdminTools()
  const close = () => { setSel(null); if (edit) navigate({ to: '/admin/tools', search: {} }) }
  return (
    <AdminShell title={m.admin_nav_tools()} description={m.admin_tools_desc()} actions={<Button onClick={() => setSel('new')}><Plus className="size-4" /> {m.admin_tool_new()}</Button>}>
      <DataTable<ToolRow>
        rows={data?.tools}
        loading={isLoading} error={isError} retrying={isFetching} onRetry={refetch}
        rowKey={(t) => t.id}
        onRow={(t) => setSel(t.id)}
        empty={m.admin_tools_empty()}
        cols={[
          { key: 'name', header: m.admin_col_name(), render: (t) => <div className="flex items-center gap-2"><ToolLogo src={t.logoUrl} className="size-7 rounded" /><div><div className="font-medium">{t.name}</div><div className="text-xs text-muted-foreground">{t.slug}</div></div></div> },
          { key: 'score', header: m.admin_col_score(), render: (t) => (t.overallScore == null ? '—' : t.overallScore.toFixed(1)), className: 'text-right tabular-nums' },
          { key: 'n', header: m.admin_tool_scores(), render: (t) => t.scoresCount ?? 0, className: 'text-right tabular-nums' },
          { key: 'v', header: m.admin_tool_videos(), render: (t) => t.videosCount ?? 0, className: 'text-right tabular-nums' },
          { key: 'pub', header: m.admin_col_published(), render: (t) => <Badge variant={t.isPublished ? 'default' : 'secondary'}>{t.isPublished ? m.admin_yes() : m.admin_no()}</Badge> },
          { key: 'sort', header: m.admin_col_sort(), render: (t) => t.sortOrder, className: 'text-right tabular-nums' },
          { key: 'created', header: m.admin_col_created(), render: (t) => fmtDate(t.createdAt) },
        ]}
      />
      <ToolEditor id={sel} onClose={close} onCreated={(id) => setSel(id)} />
    </AdminShell>
  )
}
